import PlanChange from "../PlanChange";
import Model from "../Model";
import Milestone, {MilestoneId} from "../Milestone";
import {isUUID} from "../../stores/StoreUtilities";
import ModelStore from "../../stores/ModelStore";
import {UpdatePersonInput, UpdatePlanChangeInput} from "../../API";

export class MilestoneChange {
  id: string
  label: string
  date: string
  age: number

  constructor(data: any) {
    this.id = data.id
    this.label = data.label
    this.date = data.date
    this.age = data.age
  }

  get isCustom(): boolean {
    return isUUID(this.id)
  }
}

class TimelineStrategyChange extends PlanChange {
  private _milestones: Map<string, MilestoneChange>

  constructor (data: any) {
    super(data)
    const items = this.details ? JSON.parse(this.details) : []
    this._milestones = new Map<string, MilestoneChange>(items.map((item: any) => [item.id, new MilestoneChange(item)]))
  }

  get milestones(): Map<string, MilestoneChange> {
    return this._milestones
  }

  set milestones(value: Map<string, MilestoneChange>) {
    this._milestones = structuredClone(value)
    this.details = JSON.stringify(Array.from(this._milestones.values()))
  }

  apply(model: Model) {
    const lockMsg = "Locked by Timeline Strategy"
    if (this._milestones.size > 0) {
      this._milestones.forEach((milestone: MilestoneChange, key: string) => {
        if (milestone.isCustom) {
          const m = model.milestones.get(milestone.id)
          if (m) {
            m.date = milestone.date
            model.addLock(milestone.id, "*", lockMsg)
          }
        } else if (key === MilestoneId.retirement1) {
          const person1 = model.person1
          if (person1) {
            person1.retireDate = milestone.date
            model.addLock(person1.id, "retireDate", lockMsg)
          }
        } else if (key === MilestoneId.retirement2) {
          const person2 = model.person2
          if (person2) {
            person2.retireDate = milestone.date
            model.addLock(person2.id, "retireDate", lockMsg)
          }
        } else if (key === MilestoneId.lifeExpectancy1) {
          const person1 = model.person1
          if (person1) {
            person1.hereditaryAdjust = milestone.age
            model.addLock(person1.id, "hereditaryAdjust", lockMsg)
          }
        } else if (key === MilestoneId.lifeExpectancy2) {
          const person2 = model.person2
          if (person2) {
            person2.hereditaryAdjust = milestone.age
            model.addLock(person2.id, "hereditaryAdjust", lockMsg)
          }
        }
      })

      // Recompute model milestones
      model.loadMilestones()
    }
  }

  async commit(model: Model, modelStore: ModelStore) {
    if (this._milestones.size > 0) {
      let person1Update: UpdatePersonInput | undefined = undefined
      let person2Update: UpdatePersonInput | undefined = undefined
      const milestonePromises: Promise<Milestone | undefined>[] = []
      this._milestones.forEach((milestone: MilestoneChange, key: string) => {
        if (milestone.isCustom) {
          milestonePromises.push(modelStore.updateMilestone({
            id: milestone.id,
            date: milestone.date
          }))
        } else if (key === MilestoneId.retirement1) {
          const person1 = model.person1
          if (person1) {
            person1.retireDate = milestone.date
            if (!person1Update) {
              person1Update = {
                id: person1.id,
                accountId: person1.accountId
              }
            }
            person1Update.retireDate = milestone.date
          }
        } else if (key === MilestoneId.retirement2) {
          const person2 = model.person2
          if (person2) {
            person2.retireDate = milestone.date
            if (!person2Update) {
              person2Update = {
                id: person2.id,
                accountId: person2.accountId
              }
            }
            person2Update.retireDate = milestone.date
          }
        } else if (key === MilestoneId.lifeExpectancy1) {
          const person1 = model.person1
          if (person1) {
            person1.hereditaryAdjust = milestone.age
            if (!person1Update) {
              person1Update = {
                id: person1.id,
                accountId: person1.accountId
              }
            }
            person1Update.hereditaryAdjust = milestone.age
          }
        } else if (key === MilestoneId.lifeExpectancy2) {
          const person2 = model.person2
          if (person2) {
            person2.hereditaryAdjust = milestone.age
            if (!person2Update) {
              person2Update = {
                id: person2.id,
                accountId: person2.accountId
              }
            }
            person2Update.hereditaryAdjust = milestone.age
          }
        }
      })
      if (person1Update) {
        await modelStore.updatePerson(person1Update)
      }
      if (person2Update) {
        await modelStore.updatePerson(person2Update)
      }
      await Promise.all(milestonePromises)

      // Clear out milestones
      let planChange = await modelStore.getPlanChange(this.id)
      if (planChange) {
        const input: UpdatePlanChangeInput = {
          id: planChange.id,
          description: "",
          enabled: false,
          details: JSON.stringify([])
        }
        planChange = await modelStore.updatePlanChange(input)
      }
      
      // Recompute model milestones
      model.loadMilestones()
    }
  }
}

export default TimelineStrategyChange